/**
 * Asset View Page
 * ===============
 * View IT asset details with specifications and status.
 */

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Server,
  MapPin,
  Building2,
  Trash2,
  Plus,
  RefreshCw,
  ExternalLink,
  Clock,
  Monitor,
  Ticket,
} from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import UsageGauge from '@/components/UsageGauge'
import { useAsset, useDeleteAsset, useRefreshAssetStatus } from '@/hooks/useAssets'
import { useTickets } from '@/hooks/useTickets'
import {
  ASSET_STATUS_COLORS,
  ASSET_STATUS_LABELS,
  ASSET_TYPE_LABELS,
  TICKET_STATUS_COLORS,
  TICKET_STATUS_LABELS,
  TICKET_PRIORITY_DOT_COLORS,
  IdracPSU,
  IdracTemperature,
  IdracFan,
} from '@/types'
import { formatDriveCapacity, formatStorageCapacity } from '@/utils/storage'
import { getSpeedColorClasses } from '@/utils/network'
import { openConsole, supportsConsole } from '@/utils/console'
import { CONSOLE_TYPE_LABELS } from '@/types'

export default function AssetView() {
  const navigate = useNavigate()
  const { assetId } = useParams<{ assetId: string }>()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const { data: assetData, isLoading } = useAsset(assetId ?? '')
  const deleteAsset = useDeleteAsset()
  const refreshStatus = useRefreshAssetStatus()
  const asset = assetData?.data

  // Fetch recent tickets for this asset
  const { data: ticketsData } = useTickets(
    { asset_id: asset?.id, limit: 10, sort_by: 'created_at', sort_order: 'desc' },
    { enabled: !!asset },
  )
  const activeTickets = (ticketsData?.data ?? []).filter((t) =>
    ['new', 'in_progress', 'blocked'].includes(t.status),
  )

  // Get the appropriate list route based on infrastructure_source
  const getListRoute = () => {
    switch (asset?.infrastructure_source) {
      case 'proxmox':
        return '/assets/proxmox'
      case 'synology':
        return '/assets/synology'
      case 'unifi':
        return '/assets/unifi'
      default:
        return '/assets/list'
    }
  }

  const handleDelete = async () => {
    if (!asset) return
    const listRoute = getListRoute()
    try {
      await deleteAsset.mutateAsync(asset.id)
      navigate(listRoute)
    } catch (error) {
      console.error('Failed to delete asset:', error)
    }
  }

  const handleRefreshStatus = async () => {
    if (!asset) return
    try {
      await refreshStatus.mutateAsync(asset.id)
    } catch (error) {
      console.error('Failed to refresh status:', error)
    }
  }

  // Format uptime
  const formatUptime = (seconds: number | null): string => {
    if (!seconds) return '-'
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    if (days > 0) return `${days}d ${hours}h`
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}h ${minutes}m`
  }

  // Format date
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString()
  }

  // Format relative time
  const formatLastSeen = (dateStr: string | null): string => {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes} min ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} hours ago`
    const days = Math.floor(hours / 24)
    return `${days} days ago`
  }

  // Format firmware version (strip build hash from Proxmox versions like "pve-manager/9.1.4/0283b70cb7328cdc")
  const formatFirmware = (version: string | null): string => {
    if (!version) return '-'
    // Match pattern: name/version/hash - keep only name/version
    // eslint-disable-next-line security/detect-unsafe-regex -- bounded input from API, not user-supplied
    const match = version.match(/^([^/]+\/\d+\.\d+(?:\.\d+)?)\//)
    return match ? match[1] : version
  }

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading asset...</p>
      </div>
    )
  }

  if (!asset) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Asset not found</p>
        <Button variant="secondary" onClick={() => navigate('/assets/list')} className="mt-4">
          Back to All Assets
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(getListRoute())}>
            <ArrowLeft size={20} />
          </Button>
          <div className="flex items-center gap-3">
            <span
              className={`px-3 py-1 text-sm font-medium rounded-full ${ASSET_STATUS_COLORS[asset.status]}`}
            >
              {ASSET_STATUS_LABELS[asset.status]}
            </span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{asset.name}</h1>
              <p className="text-gray-500 dark:text-gray-400">
                {asset.asset_id} • {asset.manufacturer?.name ?? 'Unknown'}{' '}
                {asset.model && `• ${asset.model}`}
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={<Ticket size={16} />}
            onClick={() => navigate(`/tickets/new?asset_id=${asset.asset_id}`)}
          >
            Create Ticket
          </Button>
          <Button
            variant="secondary"
            icon={<Plus size={16} />}
            onClick={() => navigate('/assets/new')}
          >
            Add Asset
          </Button>
          <Button
            icon={<Edit size={16} />}
            onClick={() => navigate(`/assets/${asset.asset_id}/edit`)}
          >
            Edit Asset
          </Button>
          <Button
            variant="danger"
            icon={<Trash2 size={16} />}
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Delete Asset?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete "{asset.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                loading={deleteAsset.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Specifications */}
          <Card>
            <CardHeader title="Specifications" />
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Model - always show if available */}
                {asset.model && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Model
                    </label>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {asset.model}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    CPU
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {asset.cpu_model || '-'}
                  </p>
                  {asset.cpu_cores && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {asset.cpu_cores} cores
                      {asset.cpu_threads && ` (${asset.cpu_threads} threads)`}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    RAM
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {asset.ram_gb ? `${asset.ram_gb} GB` : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Storage
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {asset.storage_capacity_gb
                      ? `${asset.storage_count ?? 1}× ${formatStorageCapacity(asset.storage_capacity_gb)} ${asset.storage_type ?? ''}`
                      : '-'}
                  </p>
                  {asset.raid_level && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {asset.raid_level}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Network
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {asset.network_speed || asset.network_ports
                      ? `${asset.network_ports ? `${asset.network_ports} ports` : ''}${asset.network_ports && asset.network_speed ? ' ' : ''}${asset.network_speed ? `(${asset.network_speed})` : ''}`
                      : '-'}
                  </p>
                </div>
                {asset.firmware_version && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      {asset.asset_type === 'nas' ? 'DSM Version' : 'Firmware'}
                    </label>
                    <p className={
                      // NAS DSM update coloring
                      asset.asset_type === 'nas' && asset.dsm_update_available === true
                        ? 'text-red-600 dark:text-red-400 font-medium'
                        : asset.asset_type === 'nas' && asset.dsm_update_available === false
                        ? 'text-green-600 dark:text-green-400'
                        // Generic firmware update coloring (UniFi, etc.)
                        : asset.firmware_update_available === true
                        ? 'text-red-600 dark:text-red-400 font-medium'
                        : asset.firmware_update_available === false
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-900 dark:text-white'
                    }>
                      <span className="inline-flex items-center gap-1.5">
                        {/* Update status dot */}
                        {asset.asset_type === 'nas' ? (
                          asset.dsm_update_available === true ? (
                            <span className="w-2 h-2 rounded-full bg-red-500 inline-block" title="Update available" />
                          ) : asset.dsm_update_available === false ? (
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" title="Up to date" />
                          ) : (
                            <span className="w-2 h-2 rounded-full bg-gray-400 inline-block" title="Unknown" />
                          )
                        ) : (
                          asset.firmware_update_available === true ? (
                            <span className="w-2 h-2 rounded-full bg-red-500 inline-block" title="Update available" />
                          ) : asset.firmware_update_available === false ? (
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" title="Up to date" />
                          ) : null
                        )}
                        {formatFirmware(asset.firmware_version)}
                      </span>
                      {asset.asset_type === 'nas' && asset.dsm_update_available === false && (
                        <span className="ml-1 text-xs text-green-600 dark:text-green-400">(Up to date)</span>
                      )}
                      {asset.asset_type !== 'nas' && asset.firmware_update_available === false && (
                        <span className="ml-1 text-xs text-green-600 dark:text-green-400">(Up to date)</span>
                      )}
                    </p>
                    {/* NAS DSM update details */}
                    {asset.asset_type === 'nas' && asset.dsm_update_available === true && asset.dsm_update_info && (() => {
                      try {
                        const info = JSON.parse(asset.dsm_update_info)
                        return (
                          <div className="mt-1 space-y-0.5">
                            {info.update?.available && info.update?.version && (
                              <p className="text-xs text-red-600 dark:text-red-400">
                                Available: {info.update.version}
                              </p>
                            )}
                            {info.security?.available && info.security?.version && (
                              <p className="text-xs text-red-600 dark:text-red-400">
                                Security: {info.security.version}
                              </p>
                            )}
                          </div>
                        )
                      } catch { return null }
                    })()}
                    {/* Generic firmware update version */}
                    {asset.asset_type !== 'nas' && asset.firmware_update_available === true && asset.firmware_update_version && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                        Available: {asset.firmware_update_version}
                      </p>
                    )}
                  </div>
                )}
                {(asset.power_watts || asset.power_supply_count) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Power
                    </label>
                    <p className="text-gray-900 dark:text-white">
                      {asset.power_watts ? `${asset.power_watts}W` : ''}
                      {asset.power_supply_count ? (
                        <span className={asset.power_watts ? 'ml-1' : ''}>
                          {asset.power_supply_count} PSU{asset.power_supply_count > 1 ? 's' : ''}
                        </span>
                      ) : null}
                      {asset.power_redundant && (asset.power_supply_count ?? 0) > 1 && (
                        <span className="ml-1 text-sm text-green-600 dark:text-green-400">
                          (Redundant)
                        </span>
                      )}
                    </p>
                  </div>
                )}
                {asset.service_tag && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Service Tag
                    </label>
                    <p className="font-mono text-gray-900 dark:text-white">
                      {asset.service_tag}
                    </p>
                  </div>
                )}
                {asset.bios_version && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      BIOS Version
                    </label>
                    <p className="text-gray-900 dark:text-white">{asset.bios_version}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Storage Drives - show if available */}
          {asset.storage_drives && asset.storage_drives.length > 0 && (
            <Card>
              <CardHeader title="Storage Drives" />
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Slot</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Size</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Model</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Serial</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Temp</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Power-On</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Life</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Health</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {asset.storage_drives.map((drive) => (
                        <tr key={drive.id}>
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-white">{drive.slot || drive.name || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{drive.drive_type?.toUpperCase() || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{formatDriveCapacity(drive.capacity_gb)}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{drive.model || '-'}</td>
                          <td className="px-3 py-2 text-sm font-mono text-gray-500 dark:text-gray-400">{drive.serial_number || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                            {drive.temperature_c != null ? (
                              <span className={drive.temperature_c >= 55 ? 'text-red-600 dark:text-red-400' : drive.temperature_c >= 45 ? 'text-yellow-600 dark:text-yellow-400' : ''}>
                                {drive.temperature_c}°C
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            {drive.power_on_hours != null ? (
                              <span title={`${drive.power_on_hours.toLocaleString()} hours${drive.bad_sectors ? `, ${drive.bad_sectors} bad sectors` : ''}`}>
                                {Math.round(drive.power_on_hours / 24 / 365 * 10) / 10}y
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-2 text-sm">
                            {drive.wear_level_percent != null ? (
                              <span
                                className={`${
                                  drive.wear_level_percent >= 80
                                    ? 'text-red-600 dark:text-red-400'
                                    : drive.wear_level_percent >= 50
                                    ? 'text-yellow-600 dark:text-yellow-400'
                                    : 'text-green-600 dark:text-green-400'
                                }`}
                                title={`${100 - drive.wear_level_percent}% life remaining`}
                              >
                                {100 - drive.wear_level_percent}%
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-2 text-sm">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              drive.health === 'good' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                              drive.health === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                              drive.health === 'critical' || drive.health === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {drive.health || 'Unknown'}
                              {drive.bad_sectors && drive.bad_sectors > 0 ? ` (${drive.bad_sectors} bad)` : ''}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Storage Volumes - show if available */}
          {asset.storage_volumes && asset.storage_volumes.length > 0 && (
            <Card>
              <CardHeader title="Storage Volumes" />
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Filesystem</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Size</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Used</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Usage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {asset.storage_volumes.map((vol) => (
                        <tr key={vol.id}>
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-white">{vol.name || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{vol.filesystem || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{formatStorageCapacity(vol.total_gb)}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{formatStorageCapacity(vol.used_gb)}</td>
                          <td className="px-3 py-2 text-sm">
                            {vol.usage_percent != null ? (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                vol.usage_percent <= 79 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                vol.usage_percent <= 90 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                                {vol.usage_percent}%
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Network Interfaces - show if available */}
          {asset.network_interfaces && asset.network_interfaces.length > 0 && (
            <Card>
              <CardHeader title="Network Interfaces" />
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Interface</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">IP Address</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden sm:table-cell">MAC Address</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Speed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {asset.network_interfaces.map((iface) => (
                        <tr key={iface.id} className={iface.link_detected === false ? 'opacity-60' : ''}>
                          <td className="px-2 py-2 text-sm font-mono text-gray-900 dark:text-white whitespace-nowrap">
                            {iface.interface_name || '-'}
                            {iface.is_primary && <span className="ml-1 px-1 py-0.5 rounded text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">P</span>}
                            {iface.is_management && <span className="ml-1 px-1 py-0.5 rounded text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">M</span>}
                          </td>
                          <td className="px-2 py-2 text-sm">
                            {iface.link_detected === true ? (
                              <span className="inline-flex items-center text-xs font-medium text-green-700 dark:text-green-400">
                                <span className="w-1.5 h-1.5 mr-1 bg-green-500 rounded-full"></span>
                                Up
                              </span>
                            ) : iface.link_detected === false ? (
                              <span className="inline-flex items-center text-xs font-medium text-gray-500 dark:text-gray-400">
                                <span className="w-1.5 h-1.5 mr-1 bg-gray-400 rounded-full"></span>
                                Down
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-sm font-mono text-gray-900 dark:text-white">{iface.ip_address || '-'}</td>
                          <td className="px-2 py-2 text-sm font-mono text-gray-500 dark:text-gray-400 hidden sm:table-cell">{iface.mac_address || '-'}</td>
                          <td className="px-2 py-2 text-sm whitespace-nowrap">
                            <span className={getSpeedColorClasses(iface.speed)[iface.link_detected === false ? 'textDimmed' : 'text']}>
                              {iface.speed || '-'}
                            </span>
                            {iface.max_speed && iface.speed !== iface.max_speed && (
                              <span className="text-gray-400 dark:text-gray-500 text-xs ml-1">/ {iface.max_speed}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Switch Port Summary - for UniFi switches with port data in network_interfaces */}
          {asset.infrastructure_source === 'unifi' && asset.network_interfaces && asset.network_interfaces.length > 0 && asset.network_interfaces.some(iface => iface.interface_name?.startsWith('Port ')) && (
            <Card>
              <CardHeader title="Switch Ports" />
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Port</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Speed</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">PoE</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {asset.network_interfaces
                        .filter(iface => iface.interface_name?.startsWith('Port '))
                        .sort((a, b) => {
                          const numA = parseInt(a.interface_name?.replace('Port ', '') ?? '0')
                          const numB = parseInt(b.interface_name?.replace('Port ', '') ?? '0')
                          return numA - numB
                        })
                        .map((port) => (
                          <tr key={port.id} className={port.link_detected === false ? 'opacity-50' : ''}>
                            <td className="px-2 py-1.5 text-sm font-mono text-gray-900 dark:text-white">{port.interface_name}</td>
                            <td className="px-2 py-1.5 text-sm">
                              {port.link_detected ? (
                                <span className="inline-flex items-center text-xs font-medium text-green-700 dark:text-green-400">
                                  <span className="w-1.5 h-1.5 mr-1 bg-green-500 rounded-full"></span>
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center text-xs font-medium text-gray-500 dark:text-gray-400">
                                  <span className="w-1.5 h-1.5 mr-1 bg-gray-400 rounded-full"></span>
                                  Inactive
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-sm">
                              <span className={getSpeedColorClasses(port.speed)[port.link_detected === false ? 'textDimmed' : 'text']}>
                                {port.speed || '-'}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-sm text-gray-900 dark:text-white">
                              {port.is_management ? (
                                <span className="text-yellow-600 dark:text-yellow-400">PoE+</span>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Power & Thermal - iDRAC data for Dell servers */}
          {(asset.idrac_temperatures || asset.idrac_psus) && (() => {
            const temps: IdracTemperature[] = asset.idrac_temperatures ? JSON.parse(asset.idrac_temperatures) : []
            const psus: IdracPSU[] = asset.idrac_psus ? JSON.parse(asset.idrac_psus) : []
            return (
              <Card>
                <CardHeader title="Power & Thermal" />
                <CardContent>
                  <div className="space-y-4">
                    {/* Temperature readings */}
                    {temps.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Temperatures</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {temps.map((temp, idx) => (
                            <div key={idx} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate" title={temp.name}>
                                {temp.name}
                              </div>
                              <div className={`text-lg font-semibold ${
                                (temp.reading_celsius ?? 0) >= 80
                                  ? 'text-red-600 dark:text-red-400'
                                  : (temp.reading_celsius ?? 0) >= 60
                                  ? 'text-yellow-600 dark:text-yellow-400'
                                  : 'text-green-600 dark:text-green-400'
                              }`}>
                                {temp.reading_celsius != null ? `${temp.reading_celsius}°C` : '-'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Power consumption */}
                    {asset.idrac_power_consumed_watts != null && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Power Consumption</h4>
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 inline-block">
                          <span className="text-2xl font-bold text-gray-900 dark:text-white">
                            {asset.idrac_power_consumed_watts}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 ml-1">W</span>
                        </div>
                      </div>
                    )}

                    {/* PSU details */}
                    {psus.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Power Supply Units</h4>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead>
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">PSU</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Output</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Capacity</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Efficiency</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {psus.map((psu, idx) => (
                                <tr key={idx}>
                                  <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{psu.name}</td>
                                  <td className="px-3 py-2 text-sm">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      psu.health === 'OK' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                      psu.health === 'Warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                      psu.health === 'Critical' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                    }`}>
                                      {psu.status || 'Unknown'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                    {psu.output_watts != null ? `${psu.output_watts}W` : '-'}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                    {psu.capacity_watts != null ? `${psu.capacity_watts}W` : '-'}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                    {psu.efficiency_rating || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })()}

          {/* Fan Status - iDRAC data for Dell servers */}
          {asset.idrac_fans && (() => {
            const fans: IdracFan[] = JSON.parse(asset.idrac_fans)
            if (fans.length === 0) return null
            return (
              <Card>
                <CardHeader title="Fan Status" />
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
                    {fans.map((fan, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2 text-center"
                        title={`${fan.name}: ${fan.reading_rpm ?? 'N/A'} RPM`}
                      >
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {fan.name.replace('System Board ', '').replace('Fan', 'F')}
                        </div>
                        <div className={`text-sm font-semibold ${
                          fan.health === 'OK'
                            ? 'text-green-600 dark:text-green-400'
                            : fan.health === 'Warning'
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : fan.health === 'Critical'
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {fan.reading_percent != null ? `${fan.reading_percent}%` : fan.reading_rpm != null ? `${fan.reading_rpm}` : '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )
          })()}

          {/* Network Information */}
          <Card>
            <CardHeader title="Network" />
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    IP Address
                  </label>
                  <p className="font-mono text-gray-900 dark:text-white">
                    {asset.ip_address || '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    MAC Address
                  </label>
                  <p className="font-mono text-gray-900 dark:text-white">
                    {asset.mac_address || '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Management IP
                  </label>
                  <p className="font-mono text-gray-900 dark:text-white">
                    {asset.management_ip || '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    VLAN
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {asset.vlan_id ?? '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Gateway
                  </label>
                  <p className="font-mono text-gray-900 dark:text-white">
                    {asset.gateway || '-'}
                  </p>
                </div>
                {asset.management_url && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Management URL
                    </label>
                    <a
                      href={asset.management_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                    >
                      Open <ExternalLink size={14} />
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status & Health */}
          <Card>
            <CardHeader
              title="Status & Health"
              action={
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<RefreshCw size={16} />}
                  onClick={handleRefreshStatus}
                  loading={refreshStatus.isPending}
                  title="Sync data from infrastructure (Proxmox/UniFi)"
                >
                  Sync
                </Button>
              }
            />
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </label>
                  <span
                    className={`inline-flex px-2 py-1 text-sm font-medium rounded-full ${ASSET_STATUS_COLORS[asset.status]}`}
                  >
                    {ASSET_STATUS_LABELS[asset.status]}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Uptime
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {formatUptime(asset.uptime_seconds)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Last Seen
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {formatLastSeen(asset.last_seen)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lifecycle */}
          <Card>
            <CardHeader title="Lifecycle" />
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Purchased
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {formatDate(asset.purchase_date)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Warranty Expiry
                  </label>
                  <p
                    className={`${
                      asset.warranty_expiry &&
                      new Date(asset.warranty_expiry) < new Date()
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {formatDate(asset.warranty_expiry)}
                    {asset.warranty_expiry &&
                      new Date(asset.warranty_expiry) < new Date() && (
                        <span className="text-xs ml-1">(Expired)</span>
                      )}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Deployed
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {formatDate(asset.deployment_date)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Cost
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {asset.cost_price ? `$${asset.cost_price}` : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {asset.notes && (
            <Card>
              <CardHeader title="Notes" />
              <CardContent>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {asset.notes}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Organization */}
          <Card>
            <CardHeader title="Organization" />
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Server className="text-gray-400" size={20} />
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Type
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {ASSET_TYPE_LABELS[asset.asset_type]}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="text-gray-400" size={20} />
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Location
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {asset.location?.name ?? '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building2 className="text-gray-400" size={20} />
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Manufacturer
                  </label>
                  <div className="flex items-center gap-2">
                    {asset.manufacturer?.logo_url && (
                      <img
                        src={asset.manufacturer.logo_url}
                        alt={asset.manufacturer.name}
                        className="h-6 w-6 object-contain"
                      />
                    )}
                    <p className="text-gray-900 dark:text-white">
                      {asset.manufacturer?.name ?? '-'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Utilization - only show for servers with usage data */}
          {asset.asset_type === 'server' && (asset.cpu_usage_percent !== null || asset.ram_usage_percent !== null) && (
            <Card>
              <CardHeader title="Utilization" />
              <CardContent>
                <div className="flex justify-around items-center py-2">
                  <UsageGauge value={asset.cpu_usage_percent} label="CPU" size="md" />
                  <UsageGauge value={asset.ram_usage_percent} label="RAM" size="md" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader title="Quick Actions" />
            <CardContent className="space-y-2">
              {/* Launch Virtual Console - for servers with console_type configured */}
              {supportsConsole(asset) && (
                <button
                  onClick={() => openConsole(asset)}
                  className="flex items-center gap-2 px-3 py-2 w-full text-left rounded-lg bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 text-primary-700 dark:text-primary-300 transition-colors"
                  title={`Open ${asset.console_type ? CONSOLE_TYPE_LABELS[asset.console_type] : 'console'} in new window`}
                >
                  <Monitor size={16} />
                  <span>Launch Console</span>
                </button>
              )}
              {asset.management_url && (
                <a
                  href={asset.management_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 w-full text-left rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <ExternalLink size={16} />
                  <span>Open Management Console</span>
                </a>
              )}
              <button
                onClick={handleRefreshStatus}
                disabled={refreshStatus.isPending}
                className="flex items-center gap-2 px-3 py-2 w-full text-left rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                title="Sync data from infrastructure (Proxmox/UniFi)"
              >
                <RefreshCw size={16} className={refreshStatus.isPending ? 'animate-spin' : ''} />
                <span>Sync from Infrastructure</span>
              </button>
            </CardContent>
          </Card>

          {/* Open Tickets */}
          {activeTickets.length > 0 && (
            <Card>
              <CardHeader
                title="Open Tickets"
                action={
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                    {activeTickets.length}
                  </span>
                }
              />
              <CardContent className="space-y-1">
                {activeTickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                    className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${TICKET_PRIORITY_DOT_COLORS[ticket.priority]}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-900 dark:text-white truncate">{ticket.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {ticket.ticket_id}
                      </p>
                    </div>
                    <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${TICKET_STATUS_COLORS[ticket.status]}`}>
                      {TICKET_STATUS_LABELS[ticket.status]}
                    </span>
                  </button>
                ))}
                <button
                  onClick={() => navigate(`/tickets?asset_id=${asset.id}`)}
                  className="w-full text-center text-xs text-primary-600 dark:text-primary-400 hover:underline pt-1"
                >
                  View all tickets
                </button>
              </CardContent>
            </Card>
          )}

          {/* Timestamps */}
          <Card>
            <CardHeader title="Record Info" />
            <CardContent className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <Clock size={14} />
                <span>Created: {formatDate(asset.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={14} />
                <span>Updated: {formatDate(asset.updated_at)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
